// import FullLoadingScreen from "@shared/components/FullLoadingScreen";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import RenderLoadingStates from "../components/RenderLoadingStates";
import { selectCommon } from "../store/slices/common";
import { logger } from "../functions/logger";
import HomeIcon from "@material-ui/icons/Home";
import PeopleIcon from "@material-ui/icons/People";
import Typography from "@material-ui/core/Typography";
import SettingsApplicationsIcon from "@material-ui/icons/SettingsApplications";
import { FaUserAlt, FaUserCog, FaUserLock } from "react-icons/fa";
import {
  useGetDebtorsQuery,
  useGetOwnerConfigQuery,
  useGetPermissionsQuery,
  useGetRouteQuery,
  useGetUserConfigQuery,
  useGetUserQuery,
} from "../store/slices/api";

const SWRFetcher = () => {
  const common = useSelector(selectCommon);

  const { creditor_id, user_id, owner_id, collect_date } = common;

  // NOTE: recovery of an empty `common` (IndexedDB eviction) now runs at the App
  // level so it fires on EVERY route, not just here — see App.jsx. This page keeps
  // the diagnostic watchdogs (swr.fetch_failed / swr.stuck_loading) below.

  const route = useGetRouteQuery({ creditor_id, user_id, collect_date });
  const user = useGetUserQuery(user_id);
  const userConfig = useGetUserConfigQuery(user_id);
  const ownerConfig = useGetOwnerConfigQuery(owner_id);
  const permissions = useGetPermissionsQuery({ creditor_id, user_id });
  const debtors = useGetDebtorsQuery({ creditor_id, user_id, collect_date });

  const { data: routeData, status: routeStatus } = route;
  const { data: userData, status: userStatus } = user;
  const { data: userConfigData, status: userConfigStatus } = userConfig;
  const { data: ownerConfigData, status: ownerConfigStatus } = ownerConfig;
  const { data: permissionsData, status: permissionsStatus } = permissions;
  const { data: debtorsData, status: debtorsStatus } = debtors;

  // This page blocks the app until all six bootstrap fetches resolve. If one
  // fails (or hangs), the page "keeps loading" with no visible reason. Log
  // exactly which query is at fault, with its error, so it can be diagnosed.
  useEffect(() => {
    const queries = [
      { name: "route", q: route, args: { creditor_id, user_id, collect_date } },
      { name: "user", q: user, args: { user_id } },
      { name: "userConfig", q: userConfig, args: { user_id } },
      { name: "ownerConfig", q: ownerConfig, args: { owner_id } },
      { name: "permissions", q: permissions, args: { creditor_id, user_id } },
      { name: "debtors", q: debtors, args: { creditor_id, user_id, collect_date } },
    ];

    queries.forEach(({ name, q, args }) => {
      if (q.isError || q.status === "rejected") {
        logger.error("swr.fetch_failed", `Bootstrap fetch failed: ${name}`, {
          entity: "swr",
          context: {
            query: name,
            args,
            status: q.status,
            httpStatus: q.error?.status,
            error: q.error?.data ?? q.error?.error ?? q.error?.message ?? String(q.error),
          },
        });
      }
    });
  }, [routeStatus, userStatus, userConfigStatus, ownerConfigStatus, permissionsStatus, debtorsStatus]);

  // Stuck-loading detector. The rejected-only log above misses the more common
  // "keeps loading forever" causes: a query stuck in "pending" (hung request) or
  // "uninitialized" (fired with an undefined arg because `common` hasn't rehydrated
  // yet). Those never set isError, so nothing would log. This snapshots the FULL
  // state of all six queries if they haven't all resolved within a grace period —
  // the timer resets on every progress/arg change, so it only fires on real stalls.
  useEffect(() => {
    const queries = [
      { name: "route", q: route, args: { creditor_id, user_id, collect_date } },
      { name: "user", q: user, args: { user_id } },
      { name: "userConfig", q: userConfig, args: { user_id } },
      { name: "ownerConfig", q: ownerConfig, args: { owner_id } },
      { name: "permissions", q: permissions, args: { creditor_id, user_id } },
      { name: "debtors", q: debtors, args: { creditor_id, user_id, collect_date } },
    ];

    const isReady = (q) => q.status === "fulfilled" && q.data != null;
    if (queries.every((x) => isReady(x.q))) return; // all done — nothing to watch

    const timer = setTimeout(() => {
      const stuck = queries.filter((x) => !isReady(x.q));
      logger.error("swr.stuck_loading", "Bootstrap loading screen stuck; not all fetches resolved", {
        entity: "swr",
        context: {
          common_hydrated: !!creditor_id,
          common: { creditor_id, user_id, owner_id, collect_date },
          stuck: stuck.map((x) => x.name),
          queries: queries.map((x) => ({
            name: x.name,
            status: x.q.status, // uninitialized | pending | fulfilled | rejected
            isError: !!x.q.isError,
            isFetching: !!x.q.isFetching,
            isUninitialized: !!x.q.isUninitialized,
            hasData: x.q.data != null,
            httpStatus: x.q.error?.status ?? null,
            error: x.q.error?.data ?? x.q.error?.error ?? x.q.error?.message ?? null,
            args: x.args,
          })),
        },
      });
    }, 20000);

    return () => clearTimeout(timer);
  }, [
    routeStatus,
    userStatus,
    userConfigStatus,
    ownerConfigStatus,
    permissionsStatus,
    debtorsStatus,
    creditor_id,
    user_id,
    owner_id,
    collect_date,
  ]);

  // Show the retry button ONLY when needed — never during a normal fast load:
  // as soon as any fetch fails, or after ~10s of not-all-ready (a real stall).
  const [showRetry, setShowRetry] = useState(false);
  useEffect(() => {
    const qs = [route, user, userConfig, ownerConfig, permissions, debtors];
    const allReady = qs.every((q) => q.status === "fulfilled" && q.data != null);
    if (allReady) {
      setShowRetry(false);
      return;
    }
    if (qs.some((q) => q.isError || q.status === "rejected")) {
      setShowRetry(true); // a fetch already failed — offer retry immediately
      return;
    }
    const t = setTimeout(() => setShowRetry(true), 10000); // still pending too long
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeStatus, userStatus, userConfigStatus, ownerConfigStatus, permissionsStatus, debtorsStatus]);

  // Manual escape hatch for flaky networks: re-fire the bootstrap fetches so a
  // stuck collector can recover without logging out/in (the only remaining cause
  // of stuck-loading is transient connectivity). Logs so we can see usage + effect.
  const handleRetry = () => {
    const notReady = [
      ["route", route],
      ["user", user],
      ["userConfig", userConfig],
      ["ownerConfig", ownerConfig],
      ["permissions", permissions],
      ["debtors", debtors],
    ]
      .filter(([, q]) => !(q.status === "fulfilled" && q.data != null))
      .map(([name]) => name);

    logger.warning("swr.manual_retry", "User tapped Reintentar on the loading screen", {
      entity: "swr",
      context: { not_ready: notReady },
    });

    route.refetch && route.refetch();
    user.refetch && user.refetch();
    userConfig.refetch && userConfig.refetch();
    ownerConfig.refetch && ownerConfig.refetch();
    permissions.refetch && permissions.refetch();
    debtors.refetch && debtors.refetch();
  };

  const data = [
    { icon: <HomeIcon />, label: "Cargando Ruta", status: routeData && routeStatus === "fulfilled" },
    {
      icon: <FaUserLock size="1.5em" />,
      label: "Cargando Permisos",
      status: permissionsData && permissionsStatus === "fulfilled",
    },
    {
      icon: <PeopleIcon />,
      label: "Cargando Clientes",
      status: debtorsData && debtorsStatus === "fulfilled",
    },
    {
      icon: <FaUserAlt size="1.4em" />,
      label: "Cargando Usuario",
      status: userData && userStatus === "fulfilled",
    },
    {
      icon: <FaUserCog size="1.7em" />,
      label: "Cargando Configuracion",
      status: userConfigData && userConfigStatus === "fulfilled",
    },
    {
      icon: <SettingsApplicationsIcon />,
      label: "Cargando Config Cuenta",
      status: ownerConfigData && ownerConfigStatus === "fulfilled",
    },
  ];

  return (
    <div className="container">
      <RenderLoadingStates data={data} />
      <Typography variant="body1" className="text-danger" paragraph>
        Por favor, mantengase conectado al internet hasta que esta pagina se quite automaticamente.
      </Typography>

      {showRetry && (
        <div className="text-center mb-4">
          <Typography variant="body2" className="text-muted" paragraph>
            La carga est&aacute; tardando. Verifique su conexi&oacute;n e intente de nuevo.
          </Typography>
          <button type="button" className="btn btn-primary" onClick={handleRetry}>
            Reintentar
          </button>
        </div>
      )}
    </div>
  );
};

export default SWRFetcher;
